import React, { useState, useRef, useEffect } from 'react';
import Icon from '@gumtree/ui-library/src/icon/icon';
import { Interpolation } from '@emotion/react';
import { isEmpty } from '@gumtree/shared/src/util/object-helpers';

import {
    DropdownContainer,
    TriggerButton,
    DropdownMenu,
    MenuItem,
    SelectFooter,
    ApplyButton,
    ClearButton,
} from './dropdown.style';

export interface DropdownOption {
    value: string | number;
    label: React.ReactNode;
    disabled?: boolean;
    divider?: boolean;
}
type DropdownValue = boolean | string | number | (string | number)[] | undefined;
export interface DropdownProps {
    options?: DropdownOption[];
    defaultValue?: DropdownValue;
    value?: DropdownValue;
    onChange?: (value: DropdownValue) => void;
    onMenuVisibleChange?: (visible?: boolean) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    type?: 'multi-select' | 'select' | 'price';
    renderOptions?: ({ closeDropdown }) => React.ReactNode;
    onVisibleChange?: (visible: boolean) => void;
    showBottom?: boolean;
    dropdownMenuCss?: Interpolation;
    onClear?: () => void;
}

const Dropdown: React.FC<DropdownProps> = ({
    options = [],
    defaultValue,
    value,
    onChange,
    placeholder = 'Please select ...',
    disabled = false,
    className,
    renderOptions,
    type = 'multi-select',
    dropdownMenuCss,
    onMenuVisibleChange,
    onClear,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState<DropdownValue>(value ?? defaultValue);
    const containerRef = useRef<HTMLDivElement>(null);
    const orignValue = useRef(value);

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            !isOpen && setSelectedValue(orignValue.current);
        }
    };
    const handleSelect = (option: DropdownOption) => {
        if (option.disabled || option.divider) return;
        let value = selectedValue;
        if (type === 'select') {
            value = option.value;
            setIsOpen(false);
        } else {
            value = [...((value as (string | number)[]) || [])];
            const index = value.indexOf(option.value);
            if (index > -1) {
                value.splice(index, 1);
            } else {
                value.push(option.value);
            }
        }
        setSelectedValue(value);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        onMenuVisibleChange && onMenuVisibleChange(isOpen);
    }, [isOpen]);

    const getSelectedLabel = () => {
        if (type !== 'select') return placeholder;
        const selectedOption = options?.find((option) => option.value === selectedValue);
        return selectedOption?.label || placeholder;
    };
    const isSelected = (value: string | number) => {
        if (type === 'multi-select') {
            return Array.isArray(selectedValue) && selectedValue.includes(value);
        }
        return selectedValue === value;
    };
    const clearAll = () => {
        setSelectedValue(undefined);
        onClear && onClear();
    };
    const apply = () => {
        orignValue.current = selectedValue;
        onChange && onChange(selectedValue);
        setIsOpen(false);
    };

    return (
        <DropdownContainer ref={containerRef} className={className}>
            <TriggerButton
                className={!isEmpty(value) || isOpen ? 'active' : ''}
                onClick={toggleDropdown}
                disabled={disabled}
            >
                <span className="label">{getSelectedLabel()}</span>
                <Icon type="chevron-d" />
            </TriggerButton>
            {isOpen && (
                <DropdownMenu css={dropdownMenuCss}>
                    {renderOptions?.({ closeDropdown: toggleDropdown }) ??
                        options?.map((option, _index) => (
                            <React.Fragment key={option.value}>
                                <MenuItem
                                    onClick={() => handleSelect(option)}
                                    className={isSelected(option.value) ? 'active' : ''}
                                >
                                    <span>{option.label}</span>
                                    <div className="check-box">
                                        <Icon type="check" color="white" />
                                    </div>
                                </MenuItem>
                            </React.Fragment>
                        ))}
                    {!renderOptions && (
                        <SelectFooter>
                            <ClearButton onClick={clearAll} label="Clear All" type="button" />
                            <ApplyButton onClick={apply} display="primary" label="Apply" />
                        </SelectFooter>
                    )}
                </DropdownMenu>
            )}
        </DropdownContainer>
    );
};

export default Dropdown;
